import { setDocument } from '@/lib/firebase/firestore';

/**
 * Stamp `lastAccessTime` on the signed-in member's own extension doc.
 *
 * The legacy app has always done this (`firebase-things.js`, inside
 * `initiateAPPUtility`), but the React rewrite never carried the line across —
 * so members who only ever use this app have no last-access record at all, and
 * the support dashboard shows them as inactive however much they use it.
 *
 * Two things matter about *whose* record this writes:
 *
 *  - It uses the REAL signed-in uid, never `getEffectiveUid()`. The effective
 *    uid resolves to the org admin for shared/team members, and stamping that
 *    would record the admin as active every time any team member opened the app.
 *  - The three server-side writers of this same field all key it by the account
 *    that owns the webhook/automation, so they describe the ORG, not a person.
 *    This write is the only per-person signal.
 */

/** Once an hour is enough to answer "when were they last here?", and avoids a
 *  write on every page load or tab reopen — the legacy app writes every time. */
const STAMP_THROTTLE_MS = 60 * 60 * 1000;

function throttleKey(uid: string, extensionName: string): string {
  return `ua_last_access_stamped_${extensionName}_${uid}`;
}

export function stampLastAccessTime(
  uid: string | null | undefined,
  extensionName: string | null | undefined
): void {
  if (!uid || !extensionName) return;

  const key = throttleKey(uid, extensionName);
  const now = Date.now();
  try {
    const previous = Number(localStorage.getItem(key) || 0);
    if (previous && now - previous < STAMP_THROTTLE_MS) return;
    // Written before the request so a slow/failed write cannot cause a retry
    // storm on every re-init; the next stamp is an hour away regardless.
    localStorage.setItem(key, String(now));
  } catch {
    // Private mode or blocked storage — fall through and stamp unthrottled
    // rather than losing the signal entirely.
  }

  // Fire and forget: app startup must never wait on, or fail because of, this.
  setDocument(`ulgebraUsers/${uid}/extensions`, extensionName, { lastAccessTime: now }).catch(
    (err) => {
      console.warn('[LastAccess] Could not stamp lastAccessTime:', err);
    }
  );
}
